import { createContext, useContext } from 'react';

/**
 * Context for DirtyTracker to expose confirmation handlers
 * @type {React.Context<{
 *   triggerConfirm: (callback: () => void, isDirty?: boolean) => void,
 *   handleProtectedAction: (action: () => void, forceCheck?: boolean) => void
 * } | null>}
 */
const DirtyTrackerContext = createContext(null);

/**
 * Hook to access DirtyTracker context
 * Provides methods to handle protected actions (like closing modals) with unsaved changes confirmation
 *
 * @returns {{
 *   triggerConfirm: (callback: () => void, isDirty?: boolean) => void,
 *   handleProtectedAction: (action: () => void, forceCheck?: boolean) => void
 * } | null}
 *
 * @example
 * const { handleProtectedAction } = useDirtyTracker();
 *
 * const handleModalClose = () => {
 *   if (handleProtectedAction) {
 *     handleProtectedAction(() => {
 *       setIsOpen(false);
 *       // other cleanup
 *     });
 *   } else {
 *     // Fallback if not wrapped in DirtyTracker
 *     setIsOpen(false);
 *   }
 * };
 */
export const useDirtyTracker = () => {
  const context = useContext(DirtyTrackerContext);
  return context;
};

export default DirtyTrackerContext;
